import {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
} from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import download from "downloadjs";

import bg1 from "@/images/storyBackground/1.png";
import bg2 from "@/images/storyBackground/2.png";
import bg3 from "@/images/storyBackground/3.png";
import bg4 from "@/images/storyBackground/4.png";

import AuthContext from "@/contexts/AuthContext";
import { useThemeMode } from "@/contexts/ThemeContext";
import { ClubsService } from "@/core/http/services/ClubsService";
import { useParams } from "react-router-dom";

const BACKGROUNDS = [bg1, bg2, bg3, bg4];
const CANVAS_W = 1080;
const CANVAS_H = 1920;
const PREVIEW_W = 270;
const PREVIEW_H = Math.round(PREVIEW_W * (CANVAS_H / CANVAS_W));
const getDefaultTextThemeByBg = (index) => (index === 0 ? "dark" : "light");

const MEDAL_COLORS = { 1: "#FFD700", 2: "#C0C0C0", 3: "#CD7F32" };
const TITLE_FONT = "Impact";

const isSameClubId = (left, right) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const getClubLogo = (club, mode) => {
  if (!club) return null;

  const lightLogo =
    club?.logoLightUrl ||
    club?.logoLight ||
    club?.logoLightMode ||
    club?.lightLogoUrl ||
    club?.logoUrl ||
    club?.logo ||
    null;
  const darkLogo =
    club?.logoDarkUrl ||
    club?.logoDark ||
    club?.logoDarkMode ||
    club?.darkLogoUrl ||
    club?.logoUrl ||
    club?.logo ||
    null;

  return mode === "dark" ? darkLogo || lightLogo : lightLogo || darkLogo;
};

const normalizeClubDetails = (data) => data?.club || data?.data || data;

const fitTextToWidth = (ctx, text, maxWidth) => {
  const value = String(text || "");
  if (ctx.measureText(value).width <= maxWidth) return value;

  let fitted = value;
  while (
    ctx.measureText(`${fitted}...`).width > maxWidth &&
    fitted.length > 3
  ) {
    fitted = fitted.slice(0, -1);
  }
  return fitted.length < value.length ? `${fitted}...` : fitted;
};

const canAppendCacheBust = (src) =>
  typeof src === "string" &&
  !src.startsWith("data:") &&
  !src.startsWith("blob:");

const withCacheBust = (src) => {
  if (!canAppendCacheBust(src)) return src;
  return `${src}${src.includes("?") ? "&" : "?"}_t=${Date.now()}`;
};

function loadCorsImage(src) {
  if (!src) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function loadFetchedImage(src) {
  if (!src) return null;

  try {
    const res = await fetch(src, { cache: "no-store" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      img.src = objectUrl;
    });
  } catch {
    return null;
  }
}

// Loads export assets without tainting the canvas.
// The original URL is tried first because some club logo URLs reject extra query params.
// Cache-bust remains as fallback for assets that were previously cached without CORS.
async function loadImage(src) {
  if (!src) return null;

  const cacheBust = withCacheBust(src);

  return (
    (await loadCorsImage(src)) ||
    (cacheBust !== src ? await loadCorsImage(cacheBust) : null) ||
    (await loadFetchedImage(src)) ||
    (cacheBust !== src ? await loadFetchedImage(cacheBust) : null)
  );
}

// Loads avatar images for the preview canvas only.
// Skips CORS so the photo always displays, at the cost of tainting the preview canvas
// (acceptable — we never call toBlob on the preview canvas).
function loadAvatarPreview(src) {
  if (!src) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function renderStory(
  canvas,
  {
    bgSrc,
    logoSrc,
    titleText,
    titleColor,
    titleFont,
    textTheme,
    players,
    pageOffset,
    categoryObj,
  },
  previewMode = false,
) {
  const ctx = canvas.getContext("2d");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;

  const isLight = textTheme === "light";
  const textColor = isLight ? "#FFFFFF" : "#1C1C1E";
  const subColor = isLight ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.52)";
  const divColor = isLight ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.10)";
  const PAD = 80;

  const loadAvatar = previewMode ? loadAvatarPreview : loadImage;
  const loadLogo = previewMode ? loadAvatarPreview : loadImage;
  const [bgImg, logoImg, ...avatarImgs] = await Promise.all([
    loadImage(bgSrc),
    loadLogo(logoSrc),
    ...players.map((p) => loadAvatar(p.avatarUrl)),
  ]);

  // Background
  if (bgImg) {
    ctx.drawImage(bgImg, 0, 0, CANVAS_W, CANVAS_H);
  } else {
    ctx.fillStyle = "#1a1a2e";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  }

  const shadow = (on) => {
    if (on) {
      ctx.shadowColor = "rgba(0,0,0,0.45)";
      ctx.shadowBlur = 14;
      ctx.shadowOffsetY = 3;
    } else {
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
    }
  };

  // ── HEADER ──────────────────────────────────────────────────
  const PAD_TOP = 80;

  const LOGO_MAX_H = 145;
  const LOGO_MAX_W = 430;

  if (logoImg) {
    const scale = Math.min(
      LOGO_MAX_W / logoImg.width,
      LOGO_MAX_H / logoImg.height,
    );
    const w = logoImg.width * scale;
    const h = logoImg.height * scale;
    shadow(true);
    ctx.drawImage(logoImg, PAD, PAD_TOP + (LOGO_MAX_H - h) / 2, w, h);
    shadow(false);
  }

  ctx.textAlign = "right";
  ctx.textBaseline = "alphabetic";
  shadow(true);
  ctx.font = `bold 92px ${titleFont}, sans-serif`;
  ctx.fillStyle = titleColor;
  ctx.fillText(titleText || "Ranking", CANVAS_W - PAD, PAD_TOP + 250);
  shadow(false);

  // Separator
  ctx.beginPath();
  ctx.moveTo(PAD, 390);
  ctx.lineTo(CANVAS_W - PAD, 390);
  ctx.strokeStyle = divColor;
  ctx.lineWidth = 2;
  ctx.stroke();

  // ── CATEGORY ────────────────────────────────────────────────
  const catName = categoryObj?.name ?? "Categoria";
  const CAT_LABEL_Y = 455;
  const CAT_NAME_Y = 535;
  const CATEGORY_DIVIDER_Y = CAT_NAME_Y + 42;
  const CAT_MAX_W = CANVAS_W - PAD * 2;

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  shadow(true);

  ctx.font = "bold 34px Arial, sans-serif";
  ctx.fillStyle = subColor;
  ctx.fillText("CATEGORIA", CANVAS_W / 2, CAT_LABEL_Y);

  ctx.font = "bold 74px Arial, sans-serif";
  ctx.fillStyle = titleColor;
  ctx.fillText(
    fitTextToWidth(ctx, catName, CAT_MAX_W),
    CANVAS_W / 2,
    CAT_NAME_Y,
  );
  shadow(false);

  ctx.beginPath();
  ctx.moveTo(PAD + 60, CATEGORY_DIVIDER_Y);
  ctx.lineTo(CANVAS_W - PAD - 60, CATEGORY_DIVIDER_Y);
  ctx.strokeStyle = divColor;
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = "bold 34px Arial, sans-serif";
  ctx.fillStyle = subColor;
  shadow(true);
  //ctx.fillText("CLASSIFICAÇÃO", CANVAS_W / 2, CLASSIFICATION_Y);
  shadow(false);

  // ── PLAYER ROWS ─────────────────────────────────────────────
  const ROW_H = 200;
  const ROW_START = CATEGORY_DIVIDER_Y + 58;
  const AVATAR_R = 62;
  const AVATAR_CX = PAD + 185;

  for (let i = 0; i < players.length; i++) {
    const player = players[i];
    const pos = pageOffset + i + 1;
    const rowY = ROW_START + i * ROW_H;
    const midY = rowY + ROW_H / 2;
    const avatarImg = avatarImgs[i] ?? null;
    const posColor = MEDAL_COLORS[pos] ?? textColor;

    // Position number
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "bold 74px Arial, sans-serif";
    ctx.fillStyle = posColor;
    shadow(true);
    ctx.fillText(`${pos}º`, PAD + 60, midY);
    shadow(false);

    // Avatar background circle
    ctx.beginPath();
    ctx.arc(AVATAR_CX, midY, AVATAR_R, 0, Math.PI * 2);
    ctx.fillStyle =
      MEDAL_COLORS[pos] ??
      (isLight ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.10)");
    ctx.fill();

    if (avatarImg) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(AVATAR_CX, midY, AVATAR_R, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(
        avatarImg,
        AVATAR_CX - AVATAR_R,
        midY - AVATAR_R,
        AVATAR_R * 2,
        AVATAR_R * 2,
      );
      ctx.restore();
    } else {
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `bold ${AVATAR_R}px Arial, sans-serif`;
      ctx.fillStyle = "#fff";
      ctx.fillText(player.name[0]?.toUpperCase() ?? "?", AVATAR_CX, midY);
    }

    // Avatar border
    ctx.beginPath();
    ctx.arc(AVATAR_CX, midY, AVATAR_R, 0, Math.PI * 2);
    ctx.strokeStyle =
      MEDAL_COLORS[pos] ??
      (isLight ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.2)");
    ctx.lineWidth = pos <= 3 ? 5 : 3;
    ctx.stroke();

    // Player name
    const nameX = PAD + 275;
    const maxNameW = CANVAS_W - PAD - nameX - 230;
    ctx.font = "bold 48px Arial, sans-serif";
    ctx.fillStyle = textColor;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    shadow(true);
    let displayName = player.name;
    while (
      ctx.measureText(displayName).width > maxNameW &&
      displayName.length > 3
    ) {
      displayName = displayName.slice(0, -1);
    }
    if (displayName.length < player.name.length) displayName += "…";
    ctx.fillText(displayName, nameX, midY);

    // Points
    ctx.textAlign = "right";
    ctx.font = "bold 54px Arial, sans-serif";
    ctx.fillStyle = subColor;
    ctx.fillText(`${player.points} pts`, CANVAS_W - PAD, midY);
    shadow(false);

    // Row divider
    if (i < players.length - 1) {
      ctx.beginPath();
      ctx.moveTo(PAD + 120, rowY + ROW_H);
      ctx.lineTo(CANVAS_W - PAD, rowY + ROW_H);
      ctx.strokeStyle = divColor;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

export default function StoryExportModal({
  open,
  onClose,
  standings = [],
  category,
  categories = [],
  rankingName = "",
}) {
  const { clubId: clubIdFromUrl } = useParams();
  const { userLoggedData } = useContext(AuthContext);
  const { mode } = useThemeMode();
  const [clubDetails, setClubDetails] = useState(null);
  const resolvedClubId =
    clubIdFromUrl || userLoggedData?.selectedClubId || null;
  const clubs = Array.isArray(userLoggedData?.clubs)
    ? userLoggedData.clubs
    : [];
  const selectedClub =
    clubs.find((c) => isSameClubId(c?.id, resolvedClubId)) || clubs[0] || null;
  const selectedClubLogo = getClubLogo(clubDetails || selectedClub, mode);

  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);

  const [bgIdx, setBgIdx] = useState(0);
  const [titleText, setTitleText] = useState("");
  const [titleColor, setTitleColor] = useState("#FF6B35");
  const [textTheme, setTextTheme] = useState(getDefaultTextThemeByBg(0));
  const [currentPage, setCurrentPage] = useState(0);
  const [rendering, setRendering] = useState(false);
  const [previewMountTick, setPreviewMountTick] = useState(0);

  useEffect(() => {
    if (rankingName && !titleText) setTitleText(rankingName);
  }, [rankingName, titleText]);

  const handleSelectBackground = useCallback((index) => {
    setBgIdx(index);
    setTextTheme(getDefaultTextThemeByBg(index));
  }, []);

  const handlePreviewCanvasRef = useCallback((node) => {
    canvasRef.current = node;
    if (node) setPreviewMountTick((tick) => tick + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchClubDetails = async () => {
      if (!resolvedClubId) {
        setClubDetails(null);
        return;
      }

      const { status, data } = await ClubsService.getById(resolvedClubId);
      if (cancelled) return;
      setClubDetails(
        status === 200 && data ? normalizeClubDetails(data) : null,
      );
    };

    fetchClubDetails();

    return () => {
      cancelled = true;
    };
  }, [resolvedClubId]);

  // Reset page when standings change
  useEffect(() => {
    setCurrentPage(0);
  }, [standings]);

  const pages = useMemo(() => {
    if (!standings.length) return [[]];
    const chunks = [];
    for (let i = 0; i < standings.length; i += 5)
      chunks.push(standings.slice(i, i + 5));
    return chunks;
  }, [standings]);

  const totalPages = pages.length;
  const categoryObj = useMemo(
    () => categories.find((c) => c.id === category),
    [categories, category],
  );

  const buildOpts = useCallback(
    (page) => ({
      bgSrc: BACKGROUNDS[bgIdx],
      logoSrc: selectedClubLogo,
      titleText: titleText || rankingName || "Ranking",
      titleColor,
      titleFont: TITLE_FONT,
      textTheme,
      players: pages[page] ?? [],
      pageOffset: page * 5,
      categoryObj,
    }),
    [
      bgIdx,
      selectedClubLogo,
      titleText,
      rankingName,
      titleColor,
      textTheme,
      pages,
      categoryObj,
    ],
  );

  // Re-render preview whenever options or page change
  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!offscreenRef.current) {
      offscreenRef.current = document.createElement("canvas");
    }
    const offscreen = offscreenRef.current;
    let cancelled = false;
    setRendering(true);

    renderStory(offscreen, buildOpts(currentPage), true)
      .then(() => {
        if (cancelled) return;
        const previewCtx = canvas.getContext("2d");
        canvas.width = PREVIEW_W;
        canvas.height = PREVIEW_H;
        previewCtx.drawImage(offscreen, 0, 0, PREVIEW_W, PREVIEW_H);
        setRendering(false);
      })
      .catch(() => {
        if (!cancelled) setRendering(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, currentPage, buildOpts, previewMountTick]);

  const handleDownload = async () => {
    setRendering(true);
    try {
      for (let p = 0; p < totalPages; p++) {
        const canvas = document.createElement("canvas");
        await renderStory(canvas, buildOpts(p));
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png"),
        );
        download(blob, `story-ranking-pagina${p + 1}.png`, "image/png");
        if (p < totalPages - 1) await new Promise((r) => setTimeout(r, 400));
      }
    } finally {
      setRendering(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogContent
        sx={{ p: 0, display: "flex", height: "90vh", maxHeight: 860 }}
      >
        {/* ── Left: preview ── */}
        <Box
          sx={{
            width: PREVIEW_W + 48,
            flexShrink: 0,
            bgcolor: "#111827",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            p: 3,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#9ca3af",
              letterSpacing: 1.5,
              fontSize: 10,
              textTransform: "uppercase",
            }}
          >
            Preview
          </Typography>

          <Box
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            }}
          >
            <canvas
              ref={handlePreviewCanvasRef}
              width={PREVIEW_W}
              height={PREVIEW_H}
              style={{ display: "block", width: PREVIEW_W, height: PREVIEW_H }}
            />
            {rendering && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "rgba(0,0,0,0.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={28} sx={{ color: "#fff" }} />
              </Box>
            )}
          </Box>

          {totalPages > 1 && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                sx={{
                  color: "#9ca3af",
                  "&.Mui-disabled": { color: "#374151" },
                }}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{ color: "#9ca3af", minWidth: 50, textAlign: "center" }}
              >
                {currentPage + 1} / {totalPages}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
                }
                disabled={currentPage === totalPages - 1}
                sx={{
                  color: "#9ca3af",
                  "&.Mui-disabled": { color: "#374151" },
                }}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Box>

        {/* ── Right: controls ── */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2.5}
          >
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Exportar para Stories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customize e exporte o ranking em formato 9:16
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack spacing={2.5} divider={<Divider />}>
            {/* Background */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Background
              </Typography>
              <Stack direction="row" spacing={1}>
                {BACKGROUNDS.map((bg, i) => (
                  <Box
                    key={i}
                    onClick={() => handleSelectBackground(i)}
                    sx={{
                      width: 62,
                      height: 110,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      border: "3px solid",
                      borderColor: bgIdx === i ? "primary.main" : "divider",
                      cursor: "pointer",
                      transition: "border-color 0.15s",
                      "&:hover": {
                        borderColor:
                          bgIdx === i ? "primary.main" : "action.focus",
                      },
                    }}
                  >
                    <img
                      src={bg}
                      alt={`BG ${i + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Title */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Título
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="Texto do título"
                  value={titleText}
                  onChange={(e) => setTitleText(e.target.value)}
                  placeholder={rankingName || "Ex: Rodada 01"}
                  fullWidth
                />
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "flex-end" }}
                >
                  <Box sx={{ minWidth: 140 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      Cor principal
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        component="input"
                        type="color"
                        value={titleColor}
                        onChange={(e) => setTitleColor(e.target.value)}
                        sx={{
                          width: 52,
                          height: 40,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          cursor: "pointer",
                          p: "3px",
                          bgcolor: "background.paper",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 700 }}
                      >
                        Títulos e destaques
                      </Typography>
                    </Stack>
                  </Box>

                  <Box sx={{ minWidth: 280 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      Cor do texto
                    </Typography>
                    <ToggleButtonGroup
                      value={textTheme}
                      exclusive
                      onChange={(_, v) => v && setTextTheme(v)}
                      size="small"
                      fullWidth
                    >
                      <ToggleButton value="light" sx={{ px: 2.5 }}>
                        Claro (branco)
                      </ToggleButton>
                      <ToggleButton value="dark" sx={{ px: 2.5 }}>
                        Escuro (preto)
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {/* Download */}
            <Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={
                  rendering ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handleDownload}
                disabled={rendering || !standings.length}
                sx={{ borderRadius: 2, fontWeight: 700, py: 1.3 }}
              >
                {rendering
                  ? "Gerando..."
                  : totalPages > 1
                    ? `Baixar ${totalPages} imagens`
                    : "Baixar imagem"}
              </Button>
              {totalPages > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={1}
                  textAlign="center"
                >
                  {totalPages} imagens serão baixadas automaticamente
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
